import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSidebarSession } from '../bridge/session';
import { CaraMark } from '../components/CaraMark';
import { ChevronLeftIcon, ChevronRightIcon, ExternalIcon, SettingsIcon } from '../components/Icons';
import { InlineError, RetryButton, SidebarState } from '../components/SidebarState';
import { PasteDock } from './PasteDock';
import { useBridgeAction } from './data';
import {
  BUCKETS_PATH,
  SETTINGS_PATH,
  SUPER_COPY_PATH,
  THIS_PAGE_PATH,
  WORKSPACES_PATH,
  bucketPath,
  workspaceGeneratePath
} from './routes';
import { useSidebarHistory } from './history';
import { bucketInitial } from './presentation';
import { LearningSessionProvider } from './learning';

const ACKNOWLEDGED_COPY_KEY = 'cara.smartfill.acknowledged-copy';

export function SidebarShell() {
  const { client, snapshot } = useSidebarSession();
  const history = useSidebarHistory();
  const location = useLocation();
  const routedSuperCopy = useRef<string | null>(null);
  const previousNavigationIntent = useRef(0);
  const previousCompletedCopy = useRef(snapshot.superCopy.completedDraftId);
  const navigationIntent = history.getIntentVersion();

  // Declared before the restoration effect: the bridge delivers a destination
  // queued before mount during subscribe, and that intent must consume
  // restoration for this copy, even when its path is already the current route.
  useEffect(
    () =>
      client.subscribe('sidebar.navigate', (destination) => {
        if (destination === 'THIS_PAGE') history.go(THIS_PAGE_PATH);
        if (destination === 'WORKSPACES') history.go(WORKSPACES_PATH);
        if (typeof destination === 'object' && destination.kind === 'GENERATE_WORKSPACE') {
          history.go(workspaceGeneratePath(destination.workspaceId));
        }
      }),
    [client, history.go]
  );

  useEffect(() => {
    const superCopy = snapshot.superCopy;
    const key = superCopy.draft?.draftId ?? superCopy.completedDraftId ?? superCopy.phase;
    const intent = history.getIntentVersion();
    const completedId = superCopy.phase === 'saved' ? superCopy.completedDraftId : null;
    // A completion arriving while the user leaves unfinished work is still unseen.
    if (
      completedId &&
      intent !== previousNavigationIntent.current &&
      (previousCompletedCopy.current === completedId || intent !== navigationIntent)
    )
      acknowledgeCopy(completedId);
    previousNavigationIntent.current = intent;
    previousCompletedCopy.current = completedId;
    if (superCopy.phase === 'idle') {
      routedSuperCopy.current = null;
    } else if (
      location.pathname === SUPER_COPY_PATH ||
      intent !== navigationIntent ||
      (completedId && acknowledgedCopy() === completedId)
    ) {
      // A tab/bridge choice made after this render wins over passive startup
      // restoration, including a same-route click that needs no new render.
      routedSuperCopy.current = key;
    } else if (routedSuperCopy.current !== key) {
      routedSuperCopy.current = key;
      history.go(SUPER_COPY_PATH, { passive: true });
    }
  }, [history, location.pathname, navigationIntent, snapshot.superCopy]);

  return (
    <main className="smartfill-sidebar">
      <SidebarNav />
      {snapshot.auth.status === 'signed-in' ? (
        <LearningSessionProvider>
          <ActiveBucketBand />
          <SidebarTabs />
          <div className="sidebar-body cara-scroll">
            <Outlet />
          </div>
          <PasteDock />
        </LearningSessionProvider>
      ) : (
        <div className="sidebar-body sidebar-body--auth cara-scroll">
          <AuthGate />
        </div>
      )}
    </main>
  );
}

function acknowledgedCopy(): string | null {
  try {
    return localStorage.getItem(ACKNOWLEDGED_COPY_KEY);
  } catch {
    return null;
  }
}

function acknowledgeCopy(completedDraftId: string): void {
  try {
    // A single opaque ID matches only this account-owned completed snapshot;
    // hosted-origin storage keeps environments separate without retaining facts.
    localStorage.setItem(ACKNOWLEDGED_COPY_KEY, completedDraftId);
  } catch {
    // Restricted embedded storage still retains this mount's navigation choice.
  }
}

function SidebarNav() {
  const history = useSidebarHistory();
  const settingsOpen = useLocation().pathname === SETTINGS_PATH;
  const toggleSettings = () => {
    if (!settingsOpen) history.go(SETTINGS_PATH);
    else if (history.canGoBack) history.goBack();
    else history.go(THIS_PAGE_PATH);
  };

  return (
    <nav className="sidebar-nav" aria-label="Sidebar navigation">
      <button
        type="button"
        className="icon-button"
        aria-label="Back"
        disabled={!history.canGoBack}
        onClick={history.goBack}
      >
        <ChevronLeftIcon />
      </button>
      <button
        type="button"
        className="icon-button"
        aria-label="Forward"
        disabled={!history.canGoForward}
        onClick={history.goForward}
      >
        <ChevronRightIcon />
      </button>
      <div className="cara-wordmark" aria-label="Cara">
        <CaraMark />
        <span>Cara</span>
      </div>
      <button
        type="button"
        className="icon-button"
        aria-label="Smart Fill settings"
        aria-pressed={settingsOpen}
        onClick={toggleSettings}
      >
        <SettingsIcon />
      </button>
    </nav>
  );
}

function ActiveBucketBand() {
  const { snapshot } = useSidebarSession();
  const history = useSidebarHistory();
  const active = snapshot.activeBucket;

  return (
    <section className={`active-bucket${active ? '' : ' active-bucket--empty'}`}>
      <button
        type="button"
        className="active-bucket__main"
        onClick={() => history.go(active ? bucketPath(active.bucketId) : BUCKETS_PATH)}
      >
        <span className="bucket-avatar">{bucketInitial(active?.label)}</span>
        <span className="active-bucket__copy">
          <span className="active-bucket__eyebrow">
            <span className="sky-dot" />
            {active ? 'Active data bucket' : 'Fill source'}
          </span>
          <strong>{active?.label ?? 'No active data bucket'}</strong>
        </span>
      </button>
      <button
        type="button"
        className="active-bucket__switch"
        onClick={() => history.go(BUCKETS_PATH)}
      >
        Switch
      </button>
    </section>
  );
}

function SidebarTabs() {
  const location = useLocation();
  const history = useSidebarHistory();
  const active = location.pathname.startsWith(BUCKETS_PATH)
    ? 'buckets'
    : location.pathname.startsWith(WORKSPACES_PATH)
      ? 'workspaces'
      : 'page';
  const tabs = [
    { key: 'page', label: 'Current Form', path: THIS_PAGE_PATH },
    { key: 'buckets', label: 'Data buckets', path: BUCKETS_PATH },
    { key: 'workspaces', label: 'Workspaces', path: WORKSPACES_PATH }
  ] as const;

  return (
    <nav className="sidebar-tabs" aria-label="Smart Fill sections">
      {tabs.map((tab) => (
        <button
          type="button"
          key={tab.key}
          className={active === tab.key ? 'is-active' : ''}
          aria-current={active === tab.key ? 'page' : undefined}
          onClick={() => history.go(tab.path)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}

function AuthGate() {
  const { client, snapshot, refresh } = useSidebarSession();
  const action = useBridgeAction();

  if (snapshot.auth.status === 'checking') {
    return (
      <SidebarState
        compact
        kind="loading"
        title="Checking your Cara session"
        description="Smart Fill will be ready when your secure Cara session is confirmed."
        action={<RetryButton onClick={refresh}>Check again</RetryButton>}
      />
    );
  }

  const expired = snapshot.auth.status === 'expired';
  const openCara = () =>
    action.run('Could not open Cara.', () =>
      client.request('cara.open', { destination: { kind: 'HOME' } })
    );

  return (
    <>
      <SidebarState
        compact
        kind="brand"
        title={expired ? 'Your Cara session expired' : 'Sign in to Cara'}
        description={
          expired
            ? 'Open Cara and sign in again, then check your session here.'
            : 'Smart Fill uses your Cara web session to access data buckets and workspaces.'
        }
        action={
          <div className="state-actions">
            <button
              type="button"
              className="button button--primary button--pill"
              disabled={action.busy}
              onClick={() => void openCara()}
            >
              <ExternalIcon />
              Sign in at Cara
            </button>
            <RetryButton onClick={refresh}>Check again</RetryButton>
          </div>
        }
      />
      {action.error && <InlineError message={action.error.message} />}
    </>
  );
}
